import {
	type TouchEventHandler,
	type UIEventHandler,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
} from 'react'
import { getContainer } from '../../utils/getContainer'
import { useDragState } from './useDragState'

// Configuration constants for drag behavior
const MODAL_SIZE_PERCENTAGE = 0.2 // 20% of modal height threshold to close the bottom sheet
const BACKDROP_OPACITY = '--backdrop-opacity' // CSS custom property for backdrop opacity

/**
 * Determines if the user is dragging on scrollable content
 * This prevents interfering with native scroll behavior
 */
const isDraggingOnScrollableContent = (target: HTMLElement) => {
	const content = target?.closest('[data-type="content"]')
	if (!content) {
		return false
	}
	const scrollContainer = content.closest('[data-draggable]')
	if (scrollContainer) {
		const { clientHeight, scrollHeight } = scrollContainer
		if (scrollHeight > clientHeight) {
			return true
		}
	}
	// Compare client height with scroll height to detect scrollable content
	return content.clientHeight !== content.scrollHeight
}

/**
 * Custom hook that enables draggable behavior for bottom sheet dialogs
 * Handles touch events to allow users to drag the sheet up/down and close it
 */
export function useDraggableBottomSheet({
	dialogRef,
	draggableRef,
	opened,
	setOpened,
}: {
	dialogRef: React.RefObject<HTMLDialogElement | null>
	draggableRef: React.RefObject<HTMLDivElement | null>
	opened: boolean
	setOpened: (opened: boolean) => void
}) {
	const {
		initialState,
		scrollLockRef,
		dragDistanceRef,
		clearDragState,
		setDragState,
	} = useDragState()

	/**
	 * Removes temporary positioning styles from dialog
	 * Resets backdrop opacity to default state
	 */
	const resetDialogPosition = useCallback(() => {
		getContainer(dialogRef.current)?.style.removeProperty('top')
		dialogRef.current?.style.removeProperty(BACKDROP_OPACITY)
	}, [dialogRef])

	/**
	 * Prevents window scroll during drag gestures
	 * Allows native scroll on scrollable content areas
	 */
	const preventWindowScroll = useCallback(
		(event: TouchEvent) => {
			const dialog = dialogRef.current
			if (!dialog?.open) {
				scrollLockRef.current = false
				return
			}
			const target = event.target as HTMLElement
			if (!scrollLockRef.current && isDraggingOnScrollableContent(target)) {
				return
			}
			event.preventDefault()
		},
		[dialogRef, scrollLockRef]
	)

	// Check if user is currently dragging (height > 0 indicates active drag)
	const isDragging = useCallback(
		() => initialState.current.height > 0,
		[initialState]
	)

	/**
	 * Determines if the content is in a scrolling state
	 * When content is scrollable, dragging is only allowed from the header
	 * This prevents conflicts between scroll and drag gestures
	 */
	const isContentScrolling = useCallback(
		(target: HTMLElement) =>
			draggableRef.current?.querySelector('[data-scrolling]') &&
			!target.closest('[data-dialog-header]'),
		[draggableRef]
	)

	/**
	 * Handles the dragging motion during touch move events
	 * Updates the sheet position and backdrop opacity in real-time
	 */
	const onTouchMove = useCallback<TouchEventHandler<HTMLElement>>(
		(e) => {
			const target = e.target as HTMLElement
			const touch = e.touches[0]
			if (!touch) {
				return
			}

			// Preserve native scroll behavior on scrollable content
			// The CSS property overscroll-behavior: contain helps with this
			if (e.cancelable && !isDraggingOnScrollableContent(target)) {
				e.preventDefault()
			}

			const dragging = isDragging()
			const isUpwardGesture = touch.clientY - initialState.current.startY < 0
			const isOnContent = !!target.closest('[data-type="content"]')

			// Allow content scrolling to resume when the gesture moves upward
			if (dragging && isOnContent && isUpwardGesture) {
				clearDragState({ startY: touch.clientY })
				resetDialogPosition()
				return
			}

			// Skip drag calculation if not actively dragging or content is scrolling
			if (!dragging || isContentScrolling(target) || !draggableRef.current) {
				return
			}

			const { offset, height } = initialState.current
			// Calculate the minimum top position (fully opened state)
			const topLimit = window.innerHeight - height
			// Calculate current top position based on touch position
			const currentTop = Math.max(touch.clientY - offset, topLimit)
			// Calculate drag distance and backdrop opacity percentage (0-1)
			const dragDistance = currentTop - topLimit
			const opacityPercentage = 1 - dragDistance / height

			// Update drag distance, backdrop opacity, and sheet position in real-time
			dragDistanceRef.current = dragDistance
			dialogRef.current?.style.setProperty(
				BACKDROP_OPACITY,
				`${opacityPercentage}`
			)
			getContainer(dialogRef.current)?.style.setProperty(
				'top',
				`${currentTop}px`
			)
		},
		[
			clearDragState,
			dialogRef,
			draggableRef,
			isContentScrolling,
			isDragging,
			resetDialogPosition,
			dragDistanceRef,
			initialState,
		]
	)

	/**
	 * Handles the end of drag gesture (touchend event)
	 * Determines whether to close the sheet or snap it back to open position
	 * Based on drag distance only
	 */
	const onTouchEnd = useCallback<TouchEventHandler<HTMLElement>>(
		(e) => {
			if (isDragging()) {
				const { height } = initialState.current
				const draggable = draggableRef.current
				if (!isContentScrolling(e.target as HTMLElement) && draggable) {
					const distance = dragDistanceRef.current

					// Threshold for closing based on drag distance (20% of modal height)
					const shouldCloseHeight = height * MODAL_SIZE_PERCENTAGE

					// Close sheet if dragged far enough
					if (distance >= shouldCloseHeight) {
						setOpened(false)
					} else {
						// Snap back to open position - remove temporary positioning
						resetDialogPosition()
					}
				}
				// Handle edge case: restore position when scrolling content while dragging
				// Keeps the sheet anchored once the user has transitioned back to scrolling
				if (isContentScrolling(e.target as HTMLElement) && draggable) {
					const topLimit = window.innerHeight - height
					getContainer(dialogRef.current)?.style.setProperty(
						'top',
						`${topLimit}px`
					)
					dialogRef.current?.style.removeProperty(BACKDROP_OPACITY)
				}
			}
			clearDragState()
		},
		[
			clearDragState,
			dialogRef,
			draggableRef,
			isContentScrolling,
			isDragging,
			resetDialogPosition,
			setOpened,
			dragDistanceRef,
			initialState,
		]
	)
	/**
	 * Initializes drag state when touch starts
	 * Records initial touch position and sheet dimensions for drag calculations
	 */
	const onTouchStart = useCallback<TouchEventHandler<HTMLElement>>(
		(e) => {
			const target = e.target as HTMLElement
			const touch = e.touches[0]
			if (!touch) {
				return
			}
			// Store initial touch position for tracking gesture direction
			initialState.current.startY = touch.clientY
			if (isContentScrolling(target)) {
				return
			}
			const height = dialogRef.current?.children[0].clientHeight ?? 0
			// Calculate initial offset from touch position to sheet bottom
			const bottom = (window.innerHeight - height - touch.clientY) * -1
			// Only initialize drag state if content is at top (not scrolled)
			if (draggableRef.current?.scrollTop === 0) {
				setDragState(bottom, height, touch.clientY)
			}
		},
		[dialogRef, draggableRef, isContentScrolling, setDragState, initialState]
	)

	const onScroll = useCallback<UIEventHandler<HTMLElement>>(
		(e) => e.preventDefault(),
		[]
	)

	/**
	 * Resets drag state when touch is cancelled by the system
	 * Ensures clean state on interruptions like incoming calls
	 */
	const onTouchCancel = useCallback<TouchEventHandler<HTMLElement>>(
		() => clearDragState(),
		[clearDragState]
	)

	/**
	 * Prevents window scroll when dragging the bottom sheet
	 * Uses passive: false to allow preventDefault on touchmove
	 */
	useEffect(() => {
		window.addEventListener('touchmove', preventWindowScroll, {
			passive: false,
		})
		return () => {
			window.removeEventListener('touchmove', preventWindowScroll)
		}
	}, [preventWindowScroll])

	/**
	 * Effects to manage touch event listeners on the dialog element
	 * Sets up/tears down dragging behavior when sheet opens/closes
	 */
	useEffect(() => {
		// Reset position and unlock scroll when sheet is closed
		if (!opened) {
			scrollLockRef.current = false
			resetDialogPosition()
		}
	}, [opened, resetDialogPosition, scrollLockRef])

	useEffect(() => {
		// Initialize clean drag state when sheet opens
		if (opened) {
			clearDragState()
		}
	}, [clearDragState, opened])

	useLayoutEffect(() => {
		// Cleanup function to reset backdrop opacity on unmount
		return () => {
			dialogRef.current?.style.removeProperty(BACKDROP_OPACITY)
		}
	}, [dialogRef])

	return useMemo(
		() => ({ onTouchStart, onTouchMove, onTouchEnd, onScroll, onTouchCancel }),
		[onTouchStart, onTouchMove, onTouchEnd, onScroll, onTouchCancel]
	)
}
